import * as React from 'react'
import { Form } from 'react-final-form'

import { useContent } from '@ngb-frontend/shared/context'
import {
  AddressFields,
  DRIVER_STEP_FORM_ID,
  StepFooterDisabledSync,
} from '@ngb-frontend/shared/ui'
import { setAddressFromPlace, string } from '@ngb-frontend/shared/utils'

import {
  ExpandingRadioCard,
  ExpandingRadioCardGroup,
} from '../../ExpandingRadioCard/ExpandingRadioCard'

import type {
  Address,
  LocationChoice,
  LocationFormValues,
} from '@ngb-frontend/shared/types'

export type LocationFormResult =
  | { choice: 'known' }
  | { choice: 'other'; address: Address }

export interface LocationFormProps {
  groupLabel: string
  testIdPrefix: string
  knownAddress?: Omit<Address, 'country_code'>
  otherDetails: string
  initialChoice?: LocationChoice
  initialAddress?: Address
  onSubmit: (result: LocationFormResult) => void
}

export const LocationForm: React.FC<LocationFormProps> = ({
  groupLabel,
  testIdPrefix,
  knownAddress,
  otherDetails,
  initialChoice,
  initialAddress,
  onSubmit,
}) => {
  const c = useContent()
  const [choice, setChoice] = React.useState<LocationChoice | undefined>(
    initialChoice === 'known' && !knownAddress ? undefined : initialChoice,
  )
  const initialValuesRef = React.useRef<LocationFormValues>({
    address: initialAddress,
  })
  const fieldsVisibleOnInit = !!Object.values(initialAddress || {}).filter(
    Boolean,
  ).length

  const handleOtherSubmit = React.useCallback(
    (values: LocationFormValues) => {
      if (values.address) onSubmit({ choice: 'other', address: values.address })
    },
    [onSubmit],
  )

  return (
    <Form<LocationFormValues>
      onSubmit={handleOtherSubmit}
      initialValues={initialValuesRef.current}
      mutators={{ setAddressFromPlace }}
    >
      {(formProps) => (
        <form
          id={DRIVER_STEP_FORM_ID}
          data-e2e-component="location-form"
          onSubmit={(event) => {
            if (choice === 'other') {
              formProps.handleSubmit(event)
              return
            }

            event.preventDefault()
            if (choice === 'known') onSubmit({ choice })
          }}
        >
          <ExpandingRadioCardGroup label={groupLabel}>
            <ExpandingRadioCard
              testId={`${testIdPrefix}-work`}
              actionName="User work"
              name="locationChoice"
              value="known"
              title={c('steps.location.work')}
              details={
                <>
                  {string([
                    knownAddress?.street,
                    knownAddress?.house_number,
                    knownAddress?.house_number_suffix,
                  ])}
                  <br />
                  {string([knownAddress?.postcode, knownAddress?.city])}
                </>
              }
              disabled={!knownAddress}
              checked={choice === 'known'}
              onChange={() => setChoice('known')}
            />
            <ExpandingRadioCard
              testId={`${testIdPrefix}-other`}
              name="locationChoice"
              value="other"
              title={c('steps.location.other')}
              details={otherDetails}
              checked={choice === 'other'}
              onChange={() => setChoice('other')}
            >
              <AddressFields
                required
                hideLabel
                fullWidth
                showFieldsOnInit={fieldsVisibleOnInit}
                onSelect={formProps.form.mutators.setAddressFromPlace}
                hideFieldsStyle="display"
              />
            </ExpandingRadioCard>
          </ExpandingRadioCardGroup>
          <StepFooterDisabledSync
            disabled={!choice || (choice === 'other' && formProps.invalid)}
          />
        </form>
      )}
    </Form>
  )
}
